import { get } from '~shared/utils/http';
import { buildSpectateUrl } from '~spa/Utils/Spectate';
import {
    getIsBogRestricted,
    getEachwayAllowed,
} from '~shared/utils/user';
import i18n from '~shared/utils/i18n';
import {
    BB_RACING_REQUEST_URL_INIT_RACING,
    BB_RACING_REQUEST_URL_SCHEDULE_RACING,
    BB_RACING_REQUEST_URL_RACECARD,
    FEATURED_TYPE,
    NEXT_RACES_TYPE,
} from './constants';
import { parseBoolean } from '~spa/Pages/Racing/schedule-widget/utils';

/**
 * Request initial widget data
 *
 * @param {String} sportSlug type of widget
 * @param {Boolean} onlyRacingMenu just get the dynamic racing menu (for racing pages)
 * @returns {Object} response json object
 */
 export const fetchInitialData = async (sportSlug, onlyRacingMenu = false) => {
    const getRacingMenu = onlyRacingMenu ? '?onlyRacingMenu=1' : '';
    const response = await get(
        buildSpectateUrl(`${BB_RACING_REQUEST_URL_INIT_RACING}${sportSlug}${getRacingMenu}`)
    );

    if (!response.ok) {
        return {};
    }

    const data = await response.json();

    return data;
};

/**
 * Fetch updated schedule
 *
 * @param {String} type type of widget
 * @param {String} raceType type of race
 * @param {Boolean} doNotGetSelectionsForHomePage only home page needs the short selection list for the racing widget
 * @returns {Object} response json object
 */
export const fetchScheduleUpdate = async (type, raceType, doNotGetSelectionsForHomePage = false) => {
    const noSelections = doNotGetSelectionsForHomePage ? '?noSelections=1' : '';
    const response = await get(
        buildSpectateUrl(
            `${BB_RACING_REQUEST_URL_SCHEDULE_RACING}${type}/${raceType}${noSelections}`
        )
    );

    if (!response.ok) {
        return {};
    }

    const data = await response.json();

    return data;
};

/**
 * Fetches racecard of the event
 *
 * @param {Number} eventId Event id
 * @returns {Object} RaceCard object
 */
export const fetchRacecardData = async eventId => {
    const response = await get(
        buildSpectateUrl(`${BB_RACING_REQUEST_URL_RACECARD}${eventId}`)
    );

    if (!response.ok) {
        return {};
    }

    const data = await response.json();

    return data;
};

/**
 * Converts data featured flag into a race type
 *
 * @param {Boolean} featured Current type of tab
 * @returns {String} Race type
 */
export const getRaceType = featured => {
    return featured ? FEATURED_TYPE : NEXT_RACES_TYPE;
};

/**
 * Get extra data for display in a racing event schedule tab, i.e. BOG, EW+, etc
 *
 * @param {*} racingData racing data
 * @param {*} eventId Event id
 * @returns {Object} Extra data
 */
export const getExtraEventData = (racingData, eventId) => {
    const eventExtra = racingData.events_extra_info[eventId];

    const isBog = !getIsBogRestricted() && Boolean(eventExtra) && Boolean(
        Number(eventExtra.best_odds_guaranteed_multiple) || Number(eventExtra.best_odds_guaranteed_single)
    );
    const eachwayPlusAllowed = getEachwayAllowed() && Boolean(eventExtra) && parseBoolean(eventExtra.each_way_plus);

    const eventDetails = racingData.events[eventId].details;

    const hasHeadline = eventDetails.headline_is_active;

    // i.e. currently selected item
    const isDefaultEvent = racingData.default_event_id === eventId;

    return {
        isDefaultEvent,
        eachwayPlusAllowed,
        isBog,
        hasHeadline,
    };
};

/**
 * Extracts translated sport name by default event id. Defaults to the sport
 * name if we do not have a translation for it.
 *
 * @param {Object} data Racing data
 * @returns {String} Name of the sport
 */
export const getSportName = data => {
    const defaultEventId = data.default_event_id;
    const defaultEvent = data.events[defaultEventId];
    const sportId = defaultEvent.details.sport_id;
    const translatedName = i18n.t(`racing.${data.sports_details[sportId].slug}`, data.sports_details[sportId].name);

    return translatedName;
};
/**
 * Gets default event id from data provided
 *
 * @param {Object} data Data object
 * @returns {Number} The event id
 */
export const getDefaultEventId = (data) => {
    return data.default_event_id ?
            data.default_event_id : data.first_betable_event_id || data.first_event_id;
};

/**
 * Updates selections of certain events
 * usually came through web socket price update
 * actions
 *
 * @param  {Array} event list of events
 * @param  {Array} selections list of selections
 * @returns {Array} Updated Selections
 */
export const updateSelections = (event, selections) => {
    let updatedSelections = [
        ...selections,
    ];

    for(const market of event.markets) {
        for(const idx in selections) {
            if(market.selections[selections[idx].id]) {
                updatedSelections[idx] = {
                    ...selections[idx],
                    ...market.selections[selections[idx].id],
                };
            }
        }
    }

    return updatedSelections;
};

/**
 * Caches list of events
 *
 * @param {Object} data data to create cached list
 * @returns {Object} cached list
 */
export const cacheEvents = data => {
    const cachedEvents = {};
    const type = getRaceType(data.featured);
    if (data.display_order.length) {
        const eventId = getDefaultEventId(data);

        cachedEvents[type] = {
            events: data.events,
            display_order: data.display_order,
            default_event_id: eventId,
            featured: data.featured,
            events_extra_info: data.events_extra_info,
            tournaments_details: data.tournaments_details,
            racecard_urls: data.racecard_urls,
        };
    }

    return cachedEvents;
};
/**
 * Creates a selections object to be stored in cache
 *
 * @param  {*} eventId Event id
 * @param  {Object} data Data came network response
 * @returns {Object} Selections ready to be cached
 */
export const cacheSelections = (eventId, data) => {
    const type = getRaceType(data.featured);

    return {
        [type]: {
            selections: {
                [eventId]: data.selections,
            },
            timeform_events: {
                ...data.timeform_events,
            },
            type_code: {
                [eventId]: data.type_code,
            },
            timeform_selections: {
                [eventId]: data.timeform_selections,
            },
        },
    };
};

/**
 * Returns a sport slug name of the only sport
 * of the widget
 *
 * @param {Array} sportsDetails a list of sports(1 sport only per widget)
 * @param {boolean} translated If we want the translated slug.
 * @returns {String} sport slug
 */
export const getSportSlug = (sportsDetails, translated = false) => {
    try {
        const [ sportsId ] = Object.keys(sportsDetails);

        if (translated) {
            return sportsDetails[sportsId].slug_i18n;
        }

        return sportsDetails[sportsId].slug;
    } catch (e) {
        return '';
    }
};
